import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Input, Label, Row, Spinner } from "reactstrap";
import ClientDetailsCard from "./Facturacion/components/ClientDetailsCard";
import ClientModal from "./Facturacion/components/ClientModal";
import DueDateModal from "./Facturacion/components/DueDateModal";
import InvoiceHeader from "./Facturacion/components/InvoiceHeader";
import ItemsTable from "./Facturacion/components/ItemsTable";
import TotalsCard from "./Facturacion/components/TotalsCard";
import PaymentsCard from "./Facturacion/components/PaymentsCard";
import {
  CREDIT_TERMS,
  ItemRow,
  ClientModalContentView,
  ClientModalView,
  ClientInfo,
  NewClientForm,
  CatalogosResponse,
  SaasEmpresa,
  SaasSucursal,
  SaasTalonario,
  PagoForm,
} from "./Facturacion/constants";
import {
  crearCliente,
  crearFactura,
  listarClientes,
  listarEmpresas,
  listarSucursales,
  listarTalonarios,
  obtenerCatalogos,
} from "./Facturacion/api";

const VentasFacturacionPage = () => {
  useEffect(() => {
    document.title = "Ventas - Facturación | Saas";
  }, []);

  const [catalogos, setCatalogos] = useState<CatalogosResponse | null>(null);
  const [empresas, setEmpresas] = useState<SaasEmpresa[]>([]);
  const [sucursales, setSucursales] = useState<SaasSucursal[]>([]);
  const [clientes, setClientes] = useState<ClientInfo[]>([]);
  const [talonarios, setTalonarios] = useState<SaasTalonario[]>([]);

  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [sucursalId, setSucursalId] = useState<number | null>(null);
  const [tipoComprobanteId, setTipoComprobanteId] = useState<number | null>(null);
  const [condicionVentaId, setCondicionVentaId] = useState<number | null>(null);
  const [talonarioId, setTalonarioId] = useState<number | null>(null);
  const [date, setDate] = useState<string>(new Date().toISOString());
  const [items, setItems] = useState<ItemRow[]>([]);
  const [dueDate, setDueDate] = useState<Date>(new Date());
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [pagos, setPagos] = useState<PagoForm[]>([]);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState<ClientInfo[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [clientModalView, setClientModalView] = useState<ClientModalView>("search");
  const [clientModalContentView, setClientModalContentView] = useState<ClientModalContentView>("search");
  const [newClientData, setNewClientData] = useState<NewClientForm>({
    razonSocial: "",
    documento: "",
    telefono: "",
    email: "",
    nombreFantasia: "",
  });
  const [nuevoClienteDocumentoTipoId, setNuevoClienteDocumentoTipoId] = useState<number | null>(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [errorInicial, setErrorInicial] = useState<string | null>(null);
  const [guardandoFactura, setGuardandoFactura] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [exitoMensaje, setExitoMensaje] = useState<string | null>(null);
  const skipFocusOpen = useRef(false);

  const crearItem = useCallback(
    (id?: number): ItemRow => ({
      id: id ?? Date.now(),
      description: "",
      quantity: 1,
      unitPrice: 0,
      unidadMedidaId: catalogos?.unidades?.[0]?.id ?? null,
      impuestoId: catalogos?.impuestos?.[0]?.id ?? null,
      descuento: 0,
    }),
    [catalogos],
  );

  const crearPago = useCallback(
    (id?: number): PagoForm => ({
      id: id ?? Date.now(),
      medioPagoId: catalogos?.medios?.[0]?.id ?? null,
      monto: 0,
      referencia: "",
    }),
    [catalogos],
  );

  const totals = useMemo(() => {
    const subtotal = items.reduce((acc, item) => acc + item.quantity * item.unitPrice, 0);
    const iva = items.reduce((acc, item) => {
      const impuesto = catalogos?.impuestos.find((imp) => imp.id === item.impuestoId);
      const tasa = impuesto?.tasa ? Number(impuesto.tasa) : 0;
      const base = Math.max(item.quantity * item.unitPrice - (item.descuento ?? 0), 0);
      return acc + base * (tasa / 100);
    }, 0);
    const total = subtotal + iva;
    return { subtotal, iva, total };
  }, [items, catalogos]);

  const totalPagos = useMemo(
    () => pagos.reduce((acc, pago) => acc + (Number(pago.monto) || 0), 0),
    [pagos],
  );

  const handleItemChange = (id: number, field: keyof ItemRow, value: number | string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                field === "description"
                  ? String(value)
                  : field === "unidadMedidaId" || field === "impuestoId"
                  ? value === "" || value === null
                    ? null
                    : Number(value)
                  : typeof value === "number"
                  ? value
                  : Number(value) || 0,
            }
          : item,
      ),
    );
  };

  const addItem = () => {
    setItems((prev) => [...prev, crearItem(prev.length + 1)]);
  };

  const removeItem = (id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handlePagoChange = (
    id: number,
    field: keyof PagoForm,
    value: string | number | null,
  ) => {
    setPagos((prev) =>
      prev.map((pago) =>
        pago.id === id
          ? {
              ...pago,
              [field]:
                field === "medioPagoId"
                  ? (value as number | null)
                  : field === "monto"
                  ? (typeof value === "number" ? value : Number(value) || 0)
                  : String(value ?? ""),
            }
          : pago,
      ),
    );
  };

  const addPago = () => {
    setPagos((prev) => [...prev, crearPago(prev.length + 1)]);
  };

  const removePago = (id: number) => {
    setPagos((prev) => (prev.length === 1 ? prev : prev.filter((pago) => pago.id !== id)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setExitoMensaje(null);
    if (!empresaId || !sucursalId || !selectedClient || !tipoComprobanteId || !condicionVentaId) {
      setFormError("Debes seleccionar empresa, sucursal, cliente y condición.");
      return;
    }
    if (!talonarioId) {
      setFormError("Selecciona un talonario disponible.");
      return;
    }
    if (!catalogos) {
      setFormError("Catálogos no disponibles.");
      return;
    }
    if (items.length === 0) {
      setFormError("Agrega al menos un ítem.");
      return;
    }
    const unidadInvalida = items.some((item) => !item.unidadMedidaId || !item.impuestoId);
    if (unidadInvalida) {
      setFormError("Cada ítem debe tener unidad e impuesto.");
      return;
    }
    if (!pagos.length) {
      setFormError("Agrega al menos un pago.");
      return;
    }
    const medioPagoInvalido = pagos.some(
      (pago) => !pago.medioPagoId || Number(pago.monto) <= 0,
    );
    if (medioPagoInvalido) {
      setFormError("Todos los pagos deben tener medio y monto mayor a cero.");
      return;
    }
    if (Number(totalPagos.toFixed(2)) < Number(totals.total.toFixed(2))) {
      setFormError("La suma de los pagos es menor al total de la factura.");
      return;
    }
    const payloadItems = items.map((item) => ({
      productoId: null,
      descripcion: item.description || "Item sin descripción",
      unidadMedidaId: item.unidadMedidaId!,
      cantidad: Number(item.quantity),
      precioUnitario: Number(item.unitPrice),
      descuento: item.descuento ?? 0,
      impuestoId: item.impuestoId!,
    }));
    const pagosPayload = pagos.map((pago) => ({
      medioPagoId: pago.medioPagoId!,
      monto: Number(pago.monto),
      referencia: pago.referencia || undefined,
    }));

    setGuardandoFactura(true);
    try {
      await crearFactura({
        empresaId,
        sucursalId,
        clienteId: selectedClient.id,
        talonarioId,
        tipoComprobanteId,
        condicionVentaId,
        fechaEmision: date,
        moneda: "PYG",
        cotizacion: 1,
        observaciones: observaciones || undefined,
        items: payloadItems,
        pagos: pagosPayload,
      });
      setExitoMensaje("Factura registrada correctamente.");
      setItems([crearItem(1)]);
      setPagos([crearPago(1)]);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "No se pudo crear la factura.");
    } finally {
      setGuardandoFactura(false);
    }
  };

  const handleCreditTerm = (days: number) => {
    const next = new Date();
    next.setDate(next.getDate() + days);
    setDueDate(next);
  };

  const condicionSeleccionada = useMemo(
    () => catalogos?.condiciones.find((c) => c.id === condicionVentaId),
    [catalogos, condicionVentaId],
  );

  const esCredito = useMemo(() => {
    if (!condicionSeleccionada) return false;
    const texto = `${condicionSeleccionada.descripcion ?? ""} ${condicionSeleccionada.codigo ?? ""}`.toLowerCase();
    return texto.includes("cr") || texto.includes("credi");
  }, [condicionSeleccionada]);

  useEffect(() => {
    if (esCredito) {
      setShowDueDateModal(true);
    } else {
      setShowDueDateModal(false);
      setDueDate(new Date());
    }
  }, [esCredito]);

  const handleClientSearch = () => {
    const term = clientSearchTerm.trim().toLowerCase();
    if (!term) {
      setClientSearchResults([]);
      return;
    }
    setClientSearchResults(
      clientes.filter(
        (client) =>
          client.razon_social.toLowerCase().includes(term) ||
          client.documento.toLowerCase().includes(term),
      ),
    );
  };

  const changeClientModalView = (view: ClientModalView) => {
    setClientModalView(view);
    if (view !== "sinNombreConfirm") {
      setClientModalContentView(view);
    }
  };

  const openClientModal = () => {
    changeClientModalView("search");
    setIsClientModalOpen(true);
  };

  const closeClientModal = () => {
    skipFocusOpen.current = true;
    window.setTimeout(() => {
      skipFocusOpen.current = false;
    }, 200);
    setIsClientModalOpen(false);
    changeClientModalView("search");
  };

  const handleClientFieldFocus = () => {
    if (!skipFocusOpen.current) {
      openClientModal();
    }
  };

  const handleClientModalToggle = () => {
    if (clientModalView === "sinNombreConfirm") {
      changeClientModalView(clientModalContentView);
      return;
    }
    if (!selectedClient) {
      changeClientModalView("sinNombreConfirm");
      return;
    }
    closeClientModal();
  };

  const applySinNombre = async () => {
    if (!catalogos?.documentoTipos.length) {
      return;
    }
    const existente = clientes.find((cliente) => cliente.documento === "SIN-RUC");
    if (existente) {
      setSelectedClient(existente);
      closeClientModal();
      return;
    }
    try {
      const nuevo = await crearCliente({
        documentoTipoId: catalogos.documentoTipos[0].id,
        documento: "SIN-RUC",
        razonSocial: "Sin nombre",
      });
      setClientes((prev) => [...prev, nuevo]);
      setSelectedClient(nuevo);
      closeClientModal();
    } catch (err) {
      window.alert(
        err instanceof Error ? err.message : "No se pudo crear el cliente sin nombre.",
      );
    }
  };

  const cargarInicial = useCallback(async () => {
    setCargandoInicial(true);
    setErrorInicial(null);
    try {
      const [catalogosData, empresasData, clientesData] = await Promise.all([
        obtenerCatalogos(),
        listarEmpresas(),
        listarClientes(),
      ]);
      setCatalogos(catalogosData);
      setEmpresas(empresasData);
      setClientes(clientesData);
      setEmpresaId(empresasData[0]?.id ?? null);
      setTipoComprobanteId(catalogosData.comprobantes?.[0]?.id ?? null);
      setCondicionVentaId(catalogosData.condiciones?.[0]?.id ?? null);
      setNuevoClienteDocumentoTipoId(catalogosData.documentoTipos?.[0]?.id ?? null);
      setItems([crearItem(1)]);
      setPagos([crearPago(1)]);
    } catch (err) {
      setErrorInicial(
        err instanceof Error ? err.message : "No se pudo cargar la información inicial.",
      );
    } finally {
      setCargandoInicial(false);
    }
  }, [crearItem]);

  useEffect(() => {
    void cargarInicial();
  }, [cargarInicial]);

  useEffect(() => {
    if (!empresaId) {
      setSucursales([]);
      setSucursalId(null);
      setTalonarios([]);
      setTalonarioId(null);
      return;
    }
    listarSucursales(empresaId)
      .then((data) => {
        setSucursales(data);
        setSucursalId(data[0]?.id ?? null);
      })
      .catch((err) => {
        setErrorInicial(
          err instanceof Error ? err.message : "No se pudieron obtener las sucursales.",
        );
        setSucursales([]);
      });
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId || !sucursalId) {
      setTalonarios([]);
      setTalonarioId(null);
      return;
    }
    listarTalonarios(empresaId, sucursalId)
      .then((data) => {
        setTalonarios(data);
        setTalonarioId(data[0]?.id ?? null);
      })
      .catch((err) => {
        setErrorInicial(
          err instanceof Error ? err.message : "No se pudieron obtener los talonarios.",
        );
        setTalonarios([]);
        setTalonarioId(null);
      });
  }, [empresaId, sucursalId]);

  useEffect(() => {
    if (!clientes.length) {
      setClientSearchResults([]);
      return;
    }
    setClientSearchResults(clientes.slice(0, 20));
  }, [clientes]);

  const selectedClientLabel = selectedClient
    ? `${selectedClient.razon_social} (${selectedClient.documento})`
    : "";
  const dueDateLabel = dueDate.toLocaleDateString("es-PY", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const handleSelectClient = (client: ClientInfo) => {
    setSelectedClient(client);
    closeClientModal();
  };

  const handleNewClientChange = (field: keyof NewClientForm, value: string) => {
    setNewClientData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveNewClient = async () => {
    if (!nuevoClienteDocumentoTipoId) {
      window.alert("Debes seleccionar el tipo de documento.");
      return;
    }
    if (!newClientData.razonSocial.trim() || !newClientData.documento.trim()) {
      window.alert("Completa la razón social y el documento del cliente.");
      return;
    }
    try {
      const nuevo = await crearCliente({
        documentoTipoId: nuevoClienteDocumentoTipoId,
        documento: newClientData.documento,
        razonSocial: newClientData.razonSocial,
        nombreFantasia: newClientData.nombreFantasia,
        email: newClientData.email,
        telefono: newClientData.telefono,
      });
      setClientes((prev) => [...prev, nuevo]);
      setSelectedClient(nuevo);
      closeClientModal();
      setNewClientData({
        razonSocial: "",
        documento: "",
        telefono: "",
        email: "",
        nombreFantasia: "",
      });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "No se pudo crear el cliente.");
    }
  };

  const handleSelectCreditTerm = (days: number) => {
    handleCreditTerm(days);
    setShowDueDateModal(false);
  };

  const handleDueDateChange = (next: Date) => {
    setDueDate(next);
  };

  if (cargandoInicial) {
    return (
      <div className="page-content">
        <Container fluid className="py-5 text-center">
          <Spinner />
          <p className="mt-3 mb-0">Cargando datos de facturación...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h4 className="fw-semibold mb-1">Facturación Corporativa</h4>
            <p className="text-muted mb-0">Automatizá ventas, cobranzas y cierre contable en un solo lugar.</p>
          </Col>
        </Row>
        {errorInicial && (
          <Alert color="danger" className="mb-3">
            {errorInicial}
          </Alert>
        )}
        {exitoMensaje && (
          <Alert color="success" className="mb-3">
            {exitoMensaje}
          </Alert>
        )}
        {formError && (
          <Alert color="danger" className="mb-3">
            {formError}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <InvoiceHeader
            empresas={empresas}
            sucursales={sucursales}
            comprobantes={catalogos?.comprobantes ?? []}
            condiciones={catalogos?.condiciones ?? []}
            talonarios={talonarios}
            empresaId={empresaId}
            sucursalId={sucursalId}
            tipoComprobanteId={tipoComprobanteId}
            condicionVentaId={condicionVentaId}
            talonarioId={talonarioId}
            emissionDate={new Date(date)}
            dueDateLabel={dueDateLabel}
            showDueDateInfo={esCredito}
            selectedClientLabel={selectedClientLabel}
            onEmpresaChange={setEmpresaId}
            onSucursalChange={setSucursalId}
            onTipoComprobanteChange={setTipoComprobanteId}
            onCondicionChange={setCondicionVentaId}
            onDateChange={(newDate) => setDate(newDate.toISOString())}
            onClientFieldFocus={handleClientFieldFocus}
            onDueDateClick={() => setShowDueDateModal(true)}
            onTalonarioChange={setTalonarioId}
          />
          <ItemsTable
            items={items}
            unidadOptions={catalogos?.unidades ?? []}
            impuestoOptions={catalogos?.impuestos ?? []}
            onItemChange={handleItemChange}
            onAddItem={addItem}
            onRemoveItem={removeItem}
          />
          <Row className="gx-3">
            <Col lg={8}>
              <ClientDetailsCard client={selectedClient} />
            </Col>
            <Col lg={4}>
              <TotalsCard subtotal={totals.subtotal} iva={totals.iva} total={totals.total} />
            </Col>
          </Row>
          <Row className="gx-3 mt-3">
            <Col lg={12}>
              <PaymentsCard
                pagos={pagos}
                medios={catalogos?.medios ?? []}
                totalFactura={totals.total}
                onChangePago={handlePagoChange}
                onAddPago={addPago}
                onRemovePago={removePago}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg={12}>
              <Label className="form-label">Observaciones</Label>
              <Input
                type="textarea"
                rows={3}
                value={observaciones}
                onChange={(event) => setObservaciones(event.target.value)}
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <Button type="submit" color="primary">
                {guardandoFactura ? "Guardando..." : "Guardar factura"}
              </Button>
              {guardandoFactura && <Spinner size="sm" className="ms-2" />}
            </Col>
          </Row>
        </Form>
        <ClientModal
          isOpen={isClientModalOpen}
          view={clientModalView}
          contentView={clientModalContentView}
          clientSearchTerm={clientSearchTerm}
          onClientSearchTermChange={setClientSearchTerm}
          onSearch={handleClientSearch}
          searchResults={clientSearchResults}
          onSelectClient={handleSelectClient}
          onToggle={handleClientModalToggle}
          onChangeView={changeClientModalView}
          newClientData={newClientData}
          onNewClientChange={handleNewClientChange}
          onSaveNewClient={handleSaveNewClient}
          onApplySinNombre={applySinNombre}
          documentoTipos={catalogos?.documentoTipos ?? []}
          documentoTipoId={nuevoClienteDocumentoTipoId}
          onDocumentoTipoChange={setNuevoClienteDocumentoTipoId}
        />
        <DueDateModal
          isOpen={showDueDateModal}
          dueDate={dueDate}
          creditTerms={CREDIT_TERMS}
          onClose={() => setShowDueDateModal(false)}
          onSelectTerm={handleSelectCreditTerm}
          onDateChange={handleDueDateChange}
        />
      </Container>
    </div>
  );
};

export default VentasFacturacionPage;
